import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { handleOnSettled, validateFloat } from '@utils/formHelpers';
import { DefaultApiResp, Material, MaterialOption } from '@root/globalTypes';
import { useForemanUpdateMaterial } from '@services/materials/foreman/update';
import { useTranslation } from 'react-i18next';
import { useUpdateMaterial } from '@services/materials/update';
import { useErrors } from '@hooks/errors';

interface UseConcreteUpdateFormProps {
  successHandler: () => void;
  material?: Material;
  setIsVisible?: (value: boolean) => void;
  foreman?: boolean;
}

interface MaterialsFormValues {
  optionsAttributes: MaterialOption[];
}

export const useConcreteUpdateForm = ({
  successHandler,
  material,
  setIsVisible,
  foreman,
}: UseConcreteUpdateFormProps) => {
  const handleErrors = useErrors();
  const { t } = useTranslation('app');

  const validateNumber = (name: string) =>
    z.custom<string>((value) => validateFloat(value), t(`forms.${name}.required`));

  const formSchema = z.object({
    optionsAttributes: z.array(
      z
        .object({
          id: z.string().optional(),
          finalQuantity: validateNumber('final'),
          finalQuantityRepeat: foreman ? validateNumber('repeatFinal') : z.string().optional(),
        })
        .refine(
          ({ finalQuantity, finalQuantityRepeat }) => {
            if (!foreman) return true;
            return Number(finalQuantity) === Number(finalQuantityRepeat);
          },
          {
            message: t('forms.repeatFinal.required'),
            path: ['finalQuantityRepeat'],
          },
        ),
    ),
  });

  const defaultValues = {
    optionsAttributes: [
      {
        id: '',
        finalQuantity: '',
        finalQuantityRepeat: '',
      },
    ],
  };

  const form = useForm<MaterialsFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues,
  });

  const fieldArray = useFieldArray({ control: form.control, name: 'optionsAttributes' });
  const { setError } = form;

  const onSettled = (resp: DefaultApiResp) => {
    handleOnSettled<DefaultApiResp>(resp, successHandler, () =>
      handleErrors(resp.errors, (key, message) =>
        setError(key as keyof MaterialsFormValues, { message }),
      ),
    );
  };

  const foremanUpdate = useForemanUpdateMaterial({ callback: onSettled });
  const materialUpdate = useUpdateMaterial({ material, callback: onSettled });

  const onSubmit = async ({ optionsAttributes }: MaterialsFormValues) => {
    if (!material) return;
    const { id, jobId, tenantId } = material;

    const parsedValues = {
      id,
      jobId,
      tenantId,
      optionsAttributes: optionsAttributes?.map((option) => ({
        id: option.id || '',
        finalQuantity: parseFloat(option.finalQuantity || '0'),
      })),
    };

    if (foreman) foremanUpdate.mutate(parsedValues);
    else materialUpdate.mutate(parsedValues);
    if (setIsVisible) setIsVisible(false);
  };

  return {
    form,
    fieldArray,
    onSubmit,
  };
};
